/**
 * mfaService.js — iki adımlı doğrulama (MFA) uçları (backlog #640, backend #639 / #4).
 *
 * GİRİŞ (açık uçlar, oturum yok; bilet kanıttır): `/login/request` bir MFA kullanıcısına `mfa_required` + `login_ticket` döner (MainContext),
 *   · POST /login/mfa/verify  login_ticket, code [, method=TOTP] → 200 { result:true, user_id, token } | 200 { result:false, data:{ ticket_valid, attempts_left } }
 *                             | 429 { data:{ locked_until } } | 503 (kod hizmeti kurulu değil)
 *   · POST /login/mfa/resend  login_ticket → 200 { result:true, phone_masked, expires_at, resend_after_seconds }
 *                             | 429 { data:{ retry_after_seconds } } çok erken / 503 SMS gidemiyor / 400 yöntem SMS değil
 *   Bilet ve kod adres çubuğuna DEĞİL form gövdesine yazılır (erişim günlüklerinde görünmesin); bilet yalnız bellekte durur.
 * PROFİL > GÜVENLİK (oturumlu, hep çağıranın kendi hesabı): GET /mfa/status, POST /mfa/sms/enroll, /mfa/sms/confirm, /mfa/totp/enroll, /mfa/totp/confirm, /mfa/disable.
 *   Zaten açık bir ikinci faktör varken yeni bir numara ya da yeni bir kimlik doğrulayıcı kurmak ve kapatmak `password` ister (400 yok / 403 yanlış / 429 5 yanlıştan sonra kilit).
 *
 * Her cevap tek biçime indirilir (`ozetle`): { ok, kind, status, message, messageKey, attemptsLeft, lockedUntil, retryAfterSeconds, ticketValid, data }.
 * `kind`: OK | WRONG_CODE | TICKET_GONE | LOCKED | TOO_SOON | UNAVAILABLE | FORBIDDEN | BAD_REQUEST | ERROR. Sunucuya hiç ulaşılamazsa (cevap yok) `ağ` hatası FIRLATILIR.
 * Metni isteğin dilinde backend yazar (`message`); sayfalar bilinen `messageKey`'leri kendi t() metniyle çizer (mfaMesajlari.js), yoksa `message`'ı gösterir.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader, getPublicHeader } from '../Components/Contexts/BackendConstants';

const LOGIN = `${JAVA_BACKEND_API_URL}/login`;
const MFA = `${JAVA_BACKEND_API_URL}/mfa`;

// Cevabın iki yazımı var: giriş uçları snake_case (ticket_valid), /mfa uçları camelCase (attemptsLeft). İkisi de okunur.
const pick = (d, a, b) => (d && d[a] !== undefined && d[a] !== null ? d[a] : d && d[b] !== undefined ? d[b] : null);

/** Bir HTTP cevabını (başarılı ya da değil) tek biçime indirger; gövdesi olmayan cevap boş sayılır. */
export function ozetle(status, body) {
  const govde = body && typeof body === 'object' ? body : {};
  const data = govde.data && typeof govde.data === 'object' ? govde.data : {};
  const ticketValid = data.ticket_valid === undefined ? null : data.ticket_valid;
  const out = {
    ok: status >= 200 && status < 300 && govde.result !== false,
    kind: 'OK',
    status,
    message: govde.message || null,
    messageKey: govde.messageKey || null,
    attemptsLeft: pick(data, 'attempts_left', 'attemptsLeft'),
    lockedUntil: pick(data, 'locked_until', 'lockedUntil'),
    retryAfterSeconds: pick(data, 'retry_after_seconds', 'retryAfterSeconds'),
    ticketValid,
    data: govde,
  };
  if (out.ok) return out;
  if (ticketValid === false) out.kind = 'TICKET_GONE';
  else if (status === 429) out.kind = out.retryAfterSeconds !== null && out.lockedUntil === null ? 'TOO_SOON' : 'LOCKED';
  else if (status === 503) out.kind = 'UNAVAILABLE';
  else if (status === 403) out.kind = 'FORBIDDEN';
  else if (status === 400) out.kind = 'BAD_REQUEST';
  else if (status === 200) out.kind = 'WRONG_CODE';
  else out.kind = 'ERROR';
  return out;
}

async function cagir(istek) {
  try {
    const response = await istek();
    return ozetle(response.status, response.data);
  } catch (error) {
    if (error && error.response) return ozetle(error.response.status, error.response.data);
    const e = new Error(error?.message || 'Network Error');
    e.network = true;
    throw e;
  }
}

function form(alanlar) {
  const p = new URLSearchParams();
  Object.entries(alanlar).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.append(k, String(v)); });
  return p;
}

/** `/login/request` cevabından ikinci adım ekranının ihtiyacı olan alanları çıkarır. Bilet yalnız bellekte tutulur. */
export function girisSorgusu(cevap) {
  return {
    method: cevap.mfa_method || 'TOTP',
    loginTicket: cevap.login_ticket,
    smsSent: cevap.sms_sent === undefined ? null : Boolean(cevap.sms_sent),
    smsMessage: cevap.sms_message || null,
    phoneMasked: cevap.phone_masked || null,
    expiresAt: cevap.expires_at || null,
    resendAfterSeconds: cevap.resend_after_seconds ?? null,
    retryAfterSeconds: cevap.retry_after_seconds ?? null,
    lockedUntil: cevap.locked_until || null,
    totpFallback: Boolean(cevap.totp_fallback),
  };
}

/** İkinci adım: kod (SMS ya da kimlik doğrulayıcı). yontem 'TOTP' ise SMS kullanıcısı kimlik doğrulayıcı uygulamasını kullanır. Başarıda ok:true ve data.user_id / data.token. */
export function girisKoduDogrula(loginTicket, kod, yontem) {
  return cagir(() => axios.post(`${LOGIN}/mfa/verify`, form({ login_ticket: loginTicket, code: kod, method: yontem === 'TOTP' ? 'TOTP' : undefined }),
    { headers: { ...getPublicHeader(), 'Content-Type': 'application/x-www-form-urlencoded' } }));
}

/** Yeni bir giriş SMS'i iste; bekleme bitmeden 429 (retryAfterSeconds). */
export function girisKoduYenidenGonder(loginTicket) {
  return cagir(() => axios.post(`${LOGIN}/mfa/resend`, form({ login_ticket: loginTicket }),
    { headers: { ...getPublicHeader(), 'Content-Type': 'application/x-www-form-urlencoded' } }));
}

export function mfaDurumu() {
  return cagir(() => axios.get(`${MFA}/status`, { headers: getAuthHeader() }));
}

/** phone yoksa profildeki numara kullanılır; ikinci faktör zaten açıksa password şart. */
export function smsKurulumuBaslat({ phone, password } = {}) {
  const govde = {};
  if (phone && phone.trim()) govde.phone = phone.trim();
  if (password) govde.password = password;
  return cagir(() => axios.post(`${MFA}/sms/enroll`, govde, { headers: getAuthHeader() }));
}

export function smsKurulumunuOnayla(kod) {
  return cagir(() => axios.post(`${MFA}/sms/confirm`, { code: kod }, { headers: getAuthHeader() }));
}

/** Yeni bir gizli anahtar üretir ({ secret, provisioningUri }); onaylanana kadar KAYITLI ESKİ ANAHTARIN YERİNİ ALIR. */
export function totpKurulumuBaslat({ password } = {}) {
  return cagir(() => axios.post(`${MFA}/totp/enroll`, password ? { password } : {}, { headers: getAuthHeader() }));
}

/** Cevap { result: boolean }: yanlış kod 200 result:false (kind WRONG_CODE). */
export function totpKurulumunuOnayla(kod) {
  return cagir(() => axios.post(`${MFA}/totp/confirm`, { code: kod }, { headers: getAuthHeader() }));
}

export function mfaKapat(password) {
  return cagir(() => axios.post(`${MFA}/disable`, { password }, { headers: getAuthHeader() }));
}

/**
 * PERSONEL SIFIRLAMASI (#611, backend #639): POST /mfa/reset/{userId} (MANAGE_USER_ROLES). Telefonunu kaybeden kullanıcının ikinci faktörü kapanır,
 * numara ve gizli anahtar unutulur, kilitler kalkar, bekleyen giriş biletleri biter; geçmişe tek satır (MFA_SIFIRLAMA) yazılır. Kökünkini yalnızca kök
 * sıfırlar (403), bilinmeyen kullanıcı 404. Diğer uçlar gibi sonuç `ok` / `message` ile gelir; ağ hatası fırlatılır.
 */
export function kullaniciMfaSifirla(userId) {
  return cagir(() => axios.post(`${MFA}/reset/${userId}`, null, { headers: getAuthHeader() }));
}
